import { Modal, ModalFooter } from '@/components/common';
import { CreateUserRequest, InstitutionDTO, UserCardDTO } from '@/models';
import React from 'react';

const EMAIL_REGEX =
  /^[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~]+(?:\.[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

interface UserFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CreateUserRequest;
  institutions: InstitutionDTO[];
  userCards: UserCardDTO[];
  isSaving: boolean;
  unlinkingCardId: string | null;
  isAssigningCard: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onUnlinkCard: (card: UserCardDTO) => void;
  onAssignCard: (tag: string, expiresAt?: string) => Promise<void>;
  onSubmit: () => void;
  onClose: () => void;
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  mode,
  formData,
  institutions,
  isSaving,
  onInputChange,
  onSubmit,
  onClose,
}) => {

  const trimmedEmail = formData.email?.trim() ?? '';
  const isEmailInvalid = trimmedEmail.length > 0 && !EMAIL_REGEX.test(trimmedEmail);


  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title={mode === 'create' ? 'Add New User' : 'Edit User'}
      size="lg"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={onSubmit}
          confirmText={mode === 'create' ? 'Create' : 'Update'}
          isLoading={isSaving}
          disabled={
            !formData.email?.trim() ||
            isEmailInvalid ||
            !formData.firstName?.trim() ||
            !formData.lastName?.trim() ||
            (mode === 'create' && !formData.institutionId)
          }
        />
      }
    >
      <form>
        <div className="row g-3">
          {mode === 'create' && (
            <div className="col-12">
              <label htmlFor="institutionId" className="form-label">
                Institution <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="institutionId"
                name="institutionId"
                value={formData.institutionId}
                onChange={onInputChange}
                required
              >
                <option value="">Select an institution...</option>
                {institutions.map((institution) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="col-md-6">
            <label htmlFor="firstName" className="form-label">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="firstName"
              name="firstName"
              value={formData.firstName || ''}
              onChange={onInputChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="lastName" className="form-label">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="lastName"
              name="lastName"
              value={formData.lastName || ''}
              onChange={onInputChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="email" className="form-label">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className={`form-control${isEmailInvalid ? ' is-invalid' : ''}`}
              id="email"
              name="email"
              value={formData.email || ''}
              onChange={onInputChange}
              required
            />
            {isEmailInvalid && (
              <div className="text-danger small mt-1">Please enter a valid email address.</div>
            )}
          </div>
          <div className="col-md-6">
            <label htmlFor="phoneNumber" className="form-label">
              Phone Number
            </label>
            <input
              type="tel"
              className="form-control"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              onChange={onInputChange}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
